import { useRef, useEffect, useCallback, useState, useImperativeHandle, forwardRef } from "react";
import { createPoseLandmarker, detectPose } from "../lib/poseEstimator";
import { getAllAngles, type Angles } from "../lib/angles";
import { drawSkeleton, drawAngles, drawContacts } from "../lib/drawSkeleton";
import { ContactDetector, type ContactState } from "../lib/contactDetector";
import Timeline from "./Timeline";
import type { PoseLandmarker, NormalizedLandmark } from "@mediapipe/tasks-vision";

export interface VideoPlayerHandle {
  setMarkIn: () => void;
  setMarkOut: () => void;
  clearMarks: () => void;
  hasLoop: () => boolean;
  seekToLoopStart: () => void;
}

interface VideoPlayerProps {
  file: File;
  isActive: boolean;
  label: string;
  playbackSpeed: number;
  playing: boolean;
  onTogglePlay: () => void;
  showSkeleton: boolean;
  showAngles: boolean;
  showContacts: boolean;
}

const isFirefox = typeof navigator !== "undefined" && /Firefox/i.test(navigator.userAgent);

const VideoPlayer = forwardRef<VideoPlayerHandle, VideoPlayerProps>(function VideoPlayer(
  { file, isActive, label, playbackSpeed, playing, onTogglePlay, showSkeleton, showAngles, showContacts },
  ref,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);
  const lastDetectTimeRef = useRef<number>(0);
  const lastLandmarksRef = useRef<NormalizedLandmark[] | null>(null);
  const lastAnglesRef = useRef<Angles | null>(null);
  const canvasSizeRef = useRef<{ w: number; h: number }>({ w: 0, h: 0 });
  const lastTimeUpdateRef = useRef<number>(0);
  const landmarkerRef = useRef<PoseLandmarker | null>(null);
  const contactDetectorRef = useRef(new ContactDetector());
  const lastContactsRef = useRef<ContactState>({ leftFoot: false, rightFoot: false });
  const [currentAngles, setCurrentAngles] = useState<Angles | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [videoUrl, setVideoUrl] = useState<string>("");
  const [codecWarning, setCodecWarning] = useState<string | null>(null);
  const [canvasPos, setCanvasPos] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  const [markIn, setMarkIn] = useState<number | null>(null);
  const [markOut, setMarkOut] = useState<number | null>(null);
  const markInRef = useRef<number | null>(null);
  const markOutRef = useRef<number | null>(null);

  useEffect(() => { markInRef.current = markIn; }, [markIn]);
  useEffect(() => { markOutRef.current = markOut; }, [markOut]);

  useEffect(() => {
    let cancelled = false;
    createPoseLandmarker()
      .then((lm) => {
        if (cancelled) { lm.close(); return; }
        landmarkerRef.current = lm;
      })
      .catch((err) => {
        console.warn("MediaPipe unavailable (pose overlay disabled):", err);
      });
    return () => {
      cancelled = true;
      if (landmarkerRef.current) {
        landmarkerRef.current.close();
        landmarkerRef.current = null;
      }
    };
  }, []);

  useImperativeHandle(ref, () => ({
    setMarkIn: () => {
      if (markInRef.current !== null) {
        setMarkIn(null);
      } else {
        const video = videoRef.current;
        if (!video) return;
        const t = video.currentTime;
        if (markOutRef.current !== null && markOutRef.current <= t) setMarkOut(null);
        setMarkIn(t);
      }
    },
    setMarkOut: () => {
      if (markOutRef.current !== null) {
        setMarkOut(null);
      } else {
        const video = videoRef.current;
        if (!video) return;
        const t = video.currentTime;
        if (markInRef.current !== null && markInRef.current >= t) setMarkIn(null);
        setMarkOut(t);
      }
    },
    clearMarks: () => { setMarkIn(null); setMarkOut(null); },
    hasLoop: () => markInRef.current !== null || markOutRef.current !== null,
    seekToLoopStart: () => {
      const video = videoRef.current;
      if (!video) return;
      video.currentTime = markInRef.current ?? 0;
    },
  }));

  const updateCanvasPos = useCallback(() => {
    const video = videoRef.current;
    const container = containerRef.current;
    if (!video || !container || !video.videoWidth || !video.videoHeight) return;

    const cw = container.clientWidth;
    const ch = container.clientHeight;
    const aspect = video.videoWidth / video.videoHeight;
    const cAspect = cw / ch;

    let rw: number, rh: number, ox: number, oy: number;
    if (aspect > cAspect) {
      rw = cw; rh = cw / aspect; ox = 0; oy = (ch - rh) / 2;
    } else {
      rh = ch; rw = ch * aspect; ox = (cw - rw) / 2; oy = 0;
    }

    setCanvasPos({ top: oy, left: ox, width: rw, height: rh });
  }, []);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setVideoUrl(url);
    setCodecWarning(null);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    if (!isFirefox || !videoUrl) return;
    const video = videoRef.current;
    if (!video) return;

    const WARNING = "This video may not play correctly in Firefox (HEVC 10-bit). Use Chrome/Safari, or convert: ffmpeg -i input.mov -c:v libx264 output.mp4";

    const onError = () => setCodecWarning(WARNING);

    const checkFrame = () => {
      if (!video.videoWidth) return;
      const c = document.createElement("canvas");
      c.width = 32;
      c.height = 32;
      const ctx = c.getContext("2d");
      if (!ctx) return;
      ctx.drawImage(video, 0, 0, 32, 32);
      const { data } = ctx.getImageData(0, 0, 32, 32);
      let greenCount = 0;
      for (let i = 0; i < data.length; i += 4) {
        if (data[i + 1] > 150 && data[i + 1] > data[i] * 2 && data[i + 1] > data[i + 2] * 2) {
          greenCount++;
        }
      }
      if (greenCount > 32 * 32 * 0.3) setCodecWarning(WARNING);
    };

    video.addEventListener("error", onError);
    video.addEventListener("loadeddata", checkFrame);
    return () => {
      video.removeEventListener("error", onError);
      video.removeEventListener("loadeddata", checkFrame);
    };
  }, [videoUrl]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => {
      updateCanvasPos();
      canvasSizeRef.current = { w: 0, h: 0 };
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [updateCanvasPos]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = playbackSpeed;
  }, [playbackSpeed]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (playing) { video.play().catch(() => {}); } else { video.pause(); }
  }, [playing]);

  const ensureCanvasSize = useCallback(
    (canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D) => {
      const dpr = window.devicePixelRatio || 1;
      const dw = canvas.clientWidth;
      const dh = canvas.clientHeight;
      const targetW = Math.round(dw * dpr);
      const targetH = Math.round(dh * dpr);
      if (canvasSizeRef.current.w !== targetW || canvasSizeRef.current.h !== targetH) {
        canvas.width = targetW;
        canvas.height = targetH;
        canvasSizeRef.current = { w: targetW, h: targetH };
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      return { displayW: dw, displayH: dh };
    },
    [],
  );

  const drawCurrentPose = useCallback((ctx: CanvasRenderingContext2D, dw: number, dh: number) => {
    ctx.clearRect(0, 0, dw, dh);
    if (lastLandmarksRef.current) {
      if (showSkeleton) {
        drawSkeleton(ctx, lastLandmarksRef.current, dw, dh);
      }
      if (showAngles && lastAnglesRef.current) {
        drawAngles(ctx, lastLandmarksRef.current, lastAnglesRef.current, dw, dh);
      }
      if (showContacts) {
        drawContacts(ctx, lastLandmarksRef.current, lastContactsRef.current, contactDetectorRef.current, dw, dh);
      }
    }
  }, [showSkeleton, showAngles, showContacts]);

  const processFrame = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.paused || video.ended) return;

    const loopEnd = markOutRef.current ?? video.duration;
    const loopStart = markInRef.current ?? 0;
    if (video.currentTime >= loopEnd) video.currentTime = loopStart;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const { displayW, displayH } = ensureCanvasSize(canvas, ctx);

    const now = performance.now();
    if (now - lastDetectTimeRef.current > 33) {
      lastDetectTimeRef.current = now;
      const result = landmarkerRef.current ? detectPose(landmarkerRef.current, video, now) : null;
      if (result) {
        lastLandmarksRef.current = result.landmarks;
        const angles = getAllAngles(result.landmarks);
        lastAnglesRef.current = angles;
        lastContactsRef.current = contactDetectorRef.current.update(result.landmarks);
        if (now - lastTimeUpdateRef.current > 100) {
          lastTimeUpdateRef.current = now;
          setCurrentAngles(angles);
          setCurrentTime(video.currentTime);
        }
      }
    }

    drawCurrentPose(ctx, displayW, displayH);
    animFrameRef.current = requestAnimationFrame(processFrame);
  }, [ensureCanvasSize, drawCurrentPose]);

  useEffect(() => {
    if (playing) {
      animFrameRef.current = requestAnimationFrame(processFrame);
    } else if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [playing, processFrame]);

  const processCurrentFrame = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    updateCanvasPos();
    const { displayW, displayH } = ensureCanvasSize(canvas, ctx);
    const now = performance.now();
    const result = landmarkerRef.current ? detectPose(landmarkerRef.current, video, now) : null;
    if (result) {
      lastLandmarksRef.current = result.landmarks;
      const angles = getAllAngles(result.landmarks);
      lastAnglesRef.current = angles;
      lastContactsRef.current = contactDetectorRef.current.update(result.landmarks);
      setCurrentAngles(angles);
    }
    drawCurrentPose(ctx, displayW, displayH);
    setCurrentTime(video.currentTime);
  }, [updateCanvasPos, ensureCanvasSize, drawCurrentPose]);

  const handleSeek = useCallback(
    (time: number) => {
      const video = videoRef.current;
      if (!video) return;
      video.currentTime = time;
      processCurrentFrame();
    },
    [processCurrentFrame],
  );

  const formatTime = (t: number) => {
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <div
      className={`flex-1 min-h-0 flex flex-col rounded-lg overflow-hidden bg-[#111] border-2 ${
        isActive ? "border-yellow-500" : "border-neutral-700"
      }`}
    >
      {/* Header */}
      <div
        className={`flex justify-between items-center px-3 py-1.5 text-[13px] ${
          isActive ? "bg-[#2a2510] text-yellow-400" : "bg-[#1a1a1a] text-gray-500"
        }`}
      >
        <span className="font-semibold">{label}</span>
      </div>

      {/* Codec warning */}
      {codecWarning && (
        <div className="flex justify-between items-center px-3 py-1.5 bg-amber-950 text-amber-500 text-xs">
          <span>{codecWarning}</span>
          <button
            onClick={() => setCodecWarning(null)}
            className="bg-transparent border-none text-amber-500 cursor-pointer text-sm px-1"
          >
            X
          </button>
        </div>
      )}

      {/* Video + Canvas overlay */}
      <div ref={containerRef} className="relative flex-1 overflow-hidden">
        <video
          ref={videoRef}
          src={videoUrl || undefined}
          onLoadedMetadata={() => {
            if (videoRef.current) {
              setDuration(videoRef.current.duration);
              updateCanvasPos();
              processCurrentFrame();
            }
          }}
          onSeeked={processCurrentFrame}
          className="w-full h-full object-contain block bg-black"
          playsInline
          muted
          loop
        />
        <canvas
          ref={canvasRef}
          className="absolute pointer-events-none z-[1]"
          style={{
            top: canvasPos?.top ?? 0,
            left: canvasPos?.left ?? 0,
            width: canvasPos?.width ?? "100%",
            height: canvasPos?.height ?? "100%",
          }}
        />
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 bg-[#1a1a1a] text-xs text-neutral-400">
        <button
          onClick={onTogglePlay}
          className="bg-transparent border border-neutral-600 rounded text-white px-2.5 py-1 sm:py-0.5 cursor-pointer text-sm touch-manipulation"
        >
          {playing ? "\u23F8" : "\u25B6"}
        </button>
        <span className="min-w-[70px] text-[11px] sm:text-xs">
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>

        <Timeline
          currentTime={currentTime}
          duration={duration}
          markIn={markIn}
          markOut={markOut}
          onSeek={handleSeek}
          onMarkInChange={setMarkIn}
          onMarkOutChange={setMarkOut}
        />

        <span className="text-[10px] text-neutral-500">Loop</span>
        <button
          onClick={() => {
            if (markIn !== null) {
              setMarkIn(null);
            } else {
              const video = videoRef.current;
              if (!video) return;
              const t = video.currentTime;
              if (markOut !== null && markOut <= t) setMarkOut(null);
              setMarkIn(t);
            }
          }}
          className={`rounded border cursor-pointer text-[11px] font-semibold px-1.5 py-1 sm:py-0.5 touch-manipulation ${
            markIn !== null
              ? "bg-green-950 border-green-600 text-green-200"
              : "bg-transparent border-neutral-600 text-gray-500"
          }`}
        >
          Start
        </button>
        <button
          onClick={() => {
            if (markOut !== null) {
              setMarkOut(null);
            } else {
              const video = videoRef.current;
              if (!video) return;
              const t = video.currentTime;
              if (markIn !== null && markIn >= t) setMarkIn(null);
              setMarkOut(t);
            }
          }}
          className={`rounded border cursor-pointer text-[11px] font-semibold px-1.5 py-1 sm:py-0.5 touch-manipulation ${
            markOut !== null
              ? "bg-green-950 border-green-600 text-green-200"
              : "bg-transparent border-neutral-600 text-gray-500"
          }`}
        >
          End
        </button>
        {(markIn !== null || markOut !== null) && (
          <button
            onClick={() => { setMarkIn(null); setMarkOut(null); }}
            className="rounded border cursor-pointer text-[11px] px-1.5 py-1 sm:py-0.5 touch-manipulation bg-transparent border-neutral-600 text-gray-500"
          >
            X
          </button>
        )}
      </div>
    </div>
  );
});

export default VideoPlayer;
